import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';
import toast from 'react-hot-toast';

const SEVERITY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  HIGH: { bg: 'bg-red-100', text: 'text-red-800', label: 'High' },
  MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
  LOW: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' },
};

const CATEGORY_LABELS: Record<string, string> = {
  PROHIBITED_BASIS: 'Prohibited Basis',
  GEOGRAPHIC_TARGETING: 'Geographic Targeting',
  STEERING: 'Steering',
  DISCOURAGEMENT: 'Discouragement',
};

interface ContentFinding {
  id: string;
  category: string;
  severity: string;
  phrase: string;
  context: string | null;
  explanation: string;
  regulatoryReference: string | null;
  status: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export default function ContentFindingsSection({ reportId }: { reportId: string }) {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewStatus, setReviewStatus] = useState('ACKNOWLEDGED');
  const [reviewNote, setReviewNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['content-findings', teamId, reportId],
    queryFn: async () => {
      const res = await fairLendingApi.getContentFindings(teamId!, { reportId });
      return res.data;
    },
    enabled: !!teamId,
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, status, reviewNote }: { id: string; status: string; reviewNote: string }) =>
      fairLendingApi.reviewContentFinding(teamId!, id, { status, reviewNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-findings', reportId] });
      toast.success('Finding reviewed');
      setReviewingId(null);
      setReviewNote('');
    },
    onError: () => toast.error('Failed to update finding'),
  });

  const findings: ContentFinding[] = data?.findings || [];

  if (isLoading || findings.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow mb-8">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <ShieldExclamationIcon className="h-5 w-5 text-ocean-600" />
          <h3 className="text-lg font-semibold text-gray-900">Fair Lending Content Screening</h3>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Rules-based screening for potential fair lending concerns in marketing language
        </p>
      </div>

      <div className="divide-y divide-gray-100">
        {findings.map((finding) => {
          const sev = SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.LOW;
          const isReviewing = reviewingId === finding.id;

          return (
            <div key={finding.id} className="px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${sev.bg} ${sev.text}`}>
                      {sev.label}
                    </span>
                    <span className="text-xs font-medium text-gray-500">
                      {CATEGORY_LABELS[finding.category] || finding.category}
                    </span>
                    {finding.status !== 'OPEN' && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
                        {finding.status.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>

                  <p className="mt-1.5 text-sm text-gray-900 font-medium">
                    &ldquo;{finding.phrase}&rdquo;
                  </p>
                  <p className="mt-1 text-sm text-gray-600">{finding.explanation}</p>

                  {finding.regulatoryReference && (
                    <p className="mt-1 text-xs text-gray-400">{finding.regulatoryReference}</p>
                  )}

                  {finding.reviewNote && (
                    <div className="mt-2 bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-500">
                        Review note: {finding.reviewNote}
                      </p>
                    </div>
                  )}
                </div>

                {finding.status === 'OPEN' && !isReviewing && (
                  <button
                    onClick={() => setReviewingId(finding.id)}
                    className="shrink-0 text-xs font-medium text-ocean-600 hover:text-ocean-700"
                  >
                    Review
                  </button>
                )}
              </div>

              {isReviewing && (
                <div className="mt-3 border-t pt-3 space-y-2">
                  <select
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
                  >
                    <option value="ACKNOWLEDGED">Acknowledged</option>
                    <option value="MITIGATED">Mitigated</option>
                    <option value="FALSE_POSITIVE">False Positive</option>
                  </select>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Add a review note..."
                    rows={2}
                    className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => { setReviewingId(null); setReviewNote(''); }}
                      className="rounded px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => reviewMutation.mutate({ id: finding.id, status: reviewStatus, reviewNote })}
                      disabled={!reviewNote.trim() || reviewMutation.isPending}
                      className="rounded px-3 py-1 text-xs font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50"
                    >
                      {reviewMutation.isPending ? 'Saving...' : 'Submit Review'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
